import { FieldType } from '@ngx-formly/material';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { debounceTime, filter, takeUntil, tap, switchMap } from 'rxjs/operators';

import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnCollection } from '@lib/common/models';

import { ISnChipsAutocompleteFormFieldConfig } from '../../interfaces/chips-autocomplete-form-field-config.interface';

@Component({
  selector: 'sn-chips-autocomplete-form-field',
  templateUrl: './chips-autocomplete-form-field.component.html',
  styleUrls: ['./chips-autocomplete-form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipsAutocompleteFormFieldComponent extends FieldType implements OnInit, OnDestroy {
  public defaultOptions: ISnChipsAutocompleteFormFieldConfig = {
    type: 'chips-autocomplete',
    templateOptions: {
      appearance: 'fill',
      valueProp: 'id',
    },
  };

  public formArray = new UntypedFormArray([]);

  public inputControl = new UntypedFormControl(null);

  private readonly _options$ = new BehaviorSubject<any[]>([]);
  private readonly _selectedOptions = new SnCollection();

  private readonly _destroy$ = new Subject<void>();

  constructor(private readonly _changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  public ngOnInit(): void {
    if (this.value?.length) {
      this.value.forEach((item) => {
        this._selectedOptions.push(item);
      });
      this._updateControlValue();
    }

    this._fetchData().pipe(takeUntil(this._destroy$)).subscribe();

    if (!this.to.fetchDataFn) {
      return;
    }

    this.to.options = this._options$;

    this._initSubscriptions();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public removeControl(index: number): void {
    const control = this.formArray.at(index);

    this._selectedOptions.delete(control.value);
    this._updateControlValue();
  }

  public trackOption(index: number, item: { label: string; value: any }): any {
    return item.value;
  }

  public onOptionSelected($event: MatAutocompleteSelectedEvent): void {
    this.inputControl.setValue(null, { emitEvent: false });

    if (this._selectedOptions.has($event.option.value)) {
      return;
    }

    const option = this._options$.value.find((item) => item.id === $event.option.value);

    this._selectedOptions.push(option);
    this._updateControlValue();

    if (this.to.change) {
      this.to.change(this.field, option);
    }
  }

  public onBlurClear(): void {
    this.inputControl.setValue(null);
    this.formControl.markAsTouched();
    this.field.focus = false;
  }

  private _fetchData(params: ISnListQueryParameters = {}): Observable<ISnListResponse<any[]>> {
    return this.to
      .fetchDataFn(params, this.model, this.field)
      .pipe(tap(({ results }) => this._options$.next([...results])));
  }

  private _updateControlValue(): void {
    const selectedOptions = [...this._selectedOptions];
    this.formArray = new UntypedFormArray(selectedOptions.map((v) => new UntypedFormControl(v)));
    this.formControl.setValue(selectedOptions);

    // TODO make work with deep strings ex key.key2.key3
    this.model[this.key.toString()] = selectedOptions;
  }

  private _initSubscriptions(): void {
    this.inputControl.valueChanges
      .pipe(
        debounceTime(300),
        filter((value) => !!value),
        switchMap((value) => this._fetchData({ search: value })),
        takeUntil(this._destroy$),
      )
      .subscribe();

    this.formControl.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((values: Record<string, any>[]) => {
        this.formArray.clear();
        this._changeDetectorRef.detectChanges();

        if (!values) {
          this._selectedOptions.clear();
          return;
        }

        values.forEach((value) => {
          this.formArray.push(new UntypedFormControl(value));
        });
      });
  }
}
